import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { GoogleLogin } from '@react-oauth/google';
import { API_BASE } from '../config';
import { loginSuccess, logout } from '../slices/authSlice';
import { invalidateOpsMeCache } from '../utils/opsApi';
import { cleanupGoogleSignInOverlays } from '../utils/googleSignInCleanup';
import '../pages/Login.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function PortalSignInFlow({
  title,
  subtitle,
  authKind,
  redirectPrefix,
  defaultRedirect,
  lookupPath,
  passwordPath,
  sendOtpPath,
  googlePath,
  otpPagePath,
  notProvisionedError,
  googleDeniedError,
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [googleError, setGoogleError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState('email');
  const [signInMethod, setSignInMethod] = useState('otp');
  const [portalStatus, setPortalStatus] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const currentAuthKind = useSelector((state) => state.auth.authKind);

  const emailTrimmed = email.trim().toLowerCase();
  const canConfirm = EMAIL_RE.test(emailTrimmed);

  useEffect(() => {
    try {
      const storedKind = sessionStorage.getItem('portal_auth_kind');
      if (storedKind && storedKind !== authKind) {
        sessionStorage.removeItem('portal_auth_email');
        sessionStorage.removeItem('portal_auth_kind');
      }
    } catch {
      // ignore storage errors
    }
  }, [authKind]);

  // Drop sessions from another portal so Google/OTP/password cannot inherit the wrong authKind.
  useEffect(() => {
    if (token && currentAuthKind && currentAuthKind !== authKind) {
      dispatch(logout());
    }
  }, [token, currentAuthKind, authKind, dispatch]);

  useEffect(() => {
    if (location.state?.opsDenied && location.state?.message) {
      setEmailError(location.state.message);
    } else if (location.state?.doctorDenied && location.state?.message) {
      setEmailError(location.state.message);
    }
  }, [location.state?.opsDenied, location.state?.doctorDenied, location.state?.message]);

  useEffect(() => {
    if (location.state?.opsDenied || location.state?.doctorDenied) return;
    if (token && currentAuthKind === authKind) {
      const from = location.state?.from;
      navigate(
        from && from.startsWith(redirectPrefix) ? from : defaultRedirect,
        { replace: true }
      );
    }
  }, [token, currentAuthKind, authKind, navigate, location.state, redirectPrefix, defaultRedirect]);

  function finishPortalLogin(data, fallbackEmail) {
    const portalKind = data.authKind || authKind;
    try {
      sessionStorage.setItem('portal_auth_kind', portalKind);
      sessionStorage.removeItem('portal_auth_email');
    } catch {
      // ignore storage errors
    }
    if (portalKind === 'ops') {
      invalidateOpsMeCache();
    }
    dispatch(
      loginSuccess({
        token: data.token,
        user: data.profile || { email: fallbackEmail },
        roles: data.roles || {
          isOps: portalKind === 'ops',
          isDoctor: portalKind === 'doctor',
        },
        authKind: portalKind,
      })
    );
    const from = location.state?.from;
    navigate(
      from && from.startsWith(redirectPrefix) ? from : defaultRedirect,
      { replace: true }
    );
  }

  function resetAuthFlow() {
    setStep('email');
    setPortalStatus(null);
    setPassword('');
    setGoogleError('');
    setEmailError('');
  }

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val);
    const t = val.trim();
    if (t.length > 0 && !EMAIL_RE.test(t)) {
      setEmailError('Enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  async function lookupPortalUser() {
    const res = await fetch(`${API_BASE}${lookupPath}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: emailTrimmed }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || `Lookup failed (${res.status})`);
    }
    return data;
  }

  async function sendOtp() {
    const res = await fetch(`${API_BASE}${sendOtpPath}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: emailTrimmed }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || `Failed to send OTP (HTTP ${res.status}).`);
    }
    sessionStorage.setItem('portal_auth_email', emailTrimmed);
    sessionStorage.setItem('portal_auth_kind', authKind);
    navigate(otpPagePath, {
      state: { portalKind: authKind, from: location.state?.from },
    });
  }

  async function handleGoogleSuccess(credentialResponse) {
    setGoogleError('');
    setEmailError('');
    try {
      try {
        sessionStorage.setItem('portal_auth_kind', authKind);
      } catch {
        // ignore storage errors
      }
      const res = await fetch(`${API_BASE}${googlePath}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.token) {
        setGoogleError(data.error || googleDeniedError);
        return;
      }
      cleanupGoogleSignInOverlays();
      finishPortalLogin(data, data.profile?.email || '');
    } catch {
      setGoogleError('Server error. Please try again or use email OTP.');
    }
  }

  async function handleContinue(e) {
    e.preventDefault();
    if (!canConfirm) return;

    setEmailError('');
    setGoogleError('');
    setIsSubmitting(true);

    try {
      const status = await lookupPortalUser();
      setPortalStatus(status);

      if (!status.exists) {
        setEmailError(notProvisionedError);
        return;
      }

      setStep('signin-methods');
      setSignInMethod(status.hasPassword ? 'password' : 'otp');
    } catch (err) {
      setEmailError(
        err?.message?.includes('Failed to fetch')
          ? 'Cannot reach the server. Start the API (e.g. port 5500) or check your network.'
          : err?.message || 'Server error. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSendSignInOtp(e) {
    e.preventDefault();
    setEmailError('');
    setIsSubmitting(true);
    try {
      await sendOtp();
    } catch (err) {
      setEmailError(err?.message || 'Failed to send OTP.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handlePasswordSignIn(e) {
    e.preventDefault();
    if (!password) {
      setEmailError('Enter your password.');
      return;
    }
    setEmailError('');
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}${passwordPath}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailTrimmed, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.token) {
        setEmailError(data.error || 'Sign in failed.');
        return;
      }
      finishPortalLogin(data, emailTrimmed);
    } catch {
      setEmailError('Server error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="proceed-page">
      <div className="proceed-bg-shapes" aria-hidden="true">
        <span className="proceed-shape proceed-shape-1" />
        <span className="proceed-shape proceed-shape-2" />
      </div>

      <Link to="/" className="proceed-logo">
        <img src="/images/unaffectedLogo.png" alt="" className="proceed-logo-image" />
        <span className="proceed-logo-text">Unaffected Healthcare</span>
      </Link>

      <div className="login-page">
        <div className="login-card-wrap">
          <div className="login-card">
            <h1 className="login-title login-title-form">
              {step === 'signin-methods' ? 'Sign in to your account' : title}
            </h1>
            <p className="login-subtitle">{subtitle}</p>

            {step === 'signin-methods' ? (
              <>
                <p className="login-subtitle" style={{ marginBottom: '0.75rem' }}>
                  Signing in as <strong>{emailTrimmed}</strong>
                </p>

                {GOOGLE_CLIENT_ID && googlePath && (
                  <div className="login-google-wrap">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => setGoogleError('Google sign-in was cancelled or failed.')}
                      useOneTap={false}
                      ux_mode="popup"
                      text="continue_with"
                      shape="rectangular"
                      width="100%"
                    />
                    <p className="login-divider">
                      <span>or use email</span>
                    </p>
                  </div>
                )}
                {googleError && (
                  <p className="login-inline-error" role="alert" style={{ marginBottom: '0.75rem' }}>
                    {googleError}
                  </p>
                )}

                <div
                  className="login-mode-toggle"
                  style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}
                >
                  <button
                    type="button"
                    className="login-btn-back"
                    style={{ flex: 1, opacity: signInMethod === 'otp' ? 1 : 0.7 }}
                    onClick={() => setSignInMethod('otp')}
                  >
                    OTP
                  </button>
                  <button
                    type="button"
                    className="login-btn-back"
                    style={{ flex: 1, opacity: signInMethod === 'password' ? 1 : 0.7 }}
                    onClick={() => setSignInMethod('password')}
                  >
                    Password
                  </button>
                </div>

                {signInMethod === 'otp' ? (
                  <form className="login-form" onSubmit={handleSendSignInOtp}>
                    <button type="submit" className="login-btn-submit" disabled={isSubmitting}>
                      {isSubmitting ? 'Sending...' : 'Send OTP to email'}
                    </button>
                  </form>
                ) : (
                  <form className="login-form" onSubmit={handlePasswordSignIn}>
                    <div className="form-group login-mobile-group">
                      <label htmlFor="portal-login-password">Password</label>
                      <input
                        id="portal-login-password"
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                      />
                    </div>
                    {!portalStatus?.hasPassword && (
                      <p className="login-inline-error" style={{ marginBottom: '0.75rem' }}>
                        No password set yet. Use OTP or ask your admin to set one.
                      </p>
                    )}
                    <button
                      type="submit"
                      className="login-btn-submit"
                      disabled={isSubmitting || !password}
                    >
                      {isSubmitting ? 'Signing in...' : 'Sign in with password'}
                    </button>
                  </form>
                )}

                {emailError && (
                  <p className="login-inline-error" role="alert" style={{ marginTop: '0.75rem' }}>
                    {emailError}
                  </p>
                )}

                <button
                  type="button"
                  className="login-link-back"
                  style={{ marginTop: '1rem', background: 'none', border: 'none', cursor: 'pointer' }}
                  onClick={resetAuthFlow}
                >
                  Use a different email
                </button>
              </>
            ) : (
              <form className="login-form" onSubmit={handleContinue}>
                <div className="form-group login-mobile-group">
                  <label htmlFor="portal-login-email">Email</label>
                  <div className="login-mobile-input-wrap">
                    <input
                      id="portal-login-email"
                      type="email"
                      inputMode="email"
                      value={email}
                      onChange={handleEmailChange}
                      placeholder="you@company.com"
                      autoComplete="email"
                      className={emailError ? 'login-input-error' : ''}
                      aria-invalid={!!emailError}
                      aria-describedby={emailError ? 'portal-login-email-error' : undefined}
                    />
                  </div>
                  {emailError && (
                    <p id="portal-login-email-error" className="login-inline-error" role="alert">
                      {emailError}
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  className="login-btn-submit"
                  disabled={!canConfirm || isSubmitting}
                >
                  {isSubmitting ? 'Checking...' : 'Continue'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
